import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { asList, getJson, money, statusClass } from '../helpers';

export default function ProjectHomePage({ projectVuid }) {
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [commitments, setCommitments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [billings, setBillings] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getJson(`/api/projects/${projectVuid}`).catch(() => null),
      getJson('/api/project-commitments').catch(() => []),
      getJson('/api/ap-invoices').catch(() => []),
      getJson('/api/project-billings').catch(() => []),
      getJson('/api/project-budgets').catch(() => []),
    ]).then(([projectData, commitmentData, invoiceData, billingData, budgetData]) => {
      if (cancelled) return;
      setProject(projectData);
      setCommitments(asList(commitmentData).filter((row) => row.project_vuid === projectVuid));
      setInvoices(asList(invoiceData).filter((row) => row.project_vuid === projectVuid));
      setBillings(asList(billingData).filter((row) => row.project_vuid === projectVuid));
      setBudgets(asList(budgetData).filter((row) => row.project_vuid === projectVuid));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  const commitmentTotal = commitments.reduce(
    (sum, row) => sum + Number(row.original_amount || row.commitment_amount || 0),
    0
  );
  const invoiceTotal = invoices.reduce((sum, row) => sum + Number(row.total_amount || 0), 0);
  const billingTotal = billings.reduce(
    (sum, row) => sum + Number(row.total_amount || row.current_payment_due || 0),
    0
  );
  const budgetTotal = budgets.reduce(
    (sum, row) => sum + Number(row.budget_amount || row.current_amount || row.original_amount || 0),
    0
  );
  const base = `/procore/projects/${projectVuid}`;

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        <Link to="/procore">Portfolio</Link>
        {' / '}
        {project?.project_name || 'Project'}
      </div>
      <div className="pc-title-row">
        <h1>{loading ? 'Home' : project?.project_name || 'Project Home'}</h1>
      </div>
      <div className="pc-grid">
        <div className="pc-card pc-stat">
          <div className="pc-label">Current Budget</div>
          <div className="pc-value">{money(budgetTotal || project?.current_budget)}</div>
          <Link to={`${base}/budget`}>Open Budget</Link>
        </div>
        <div className="pc-card pc-stat">
          <div className="pc-label">Commitments</div>
          <div className="pc-value">{money(commitmentTotal)}</div>
          <Link to={`${base}/commitments`}>{commitments.length} contract{commitments.length === 1 ? '' : 's'}</Link>
        </div>
        <div className="pc-card pc-stat">
          <div className="pc-label">Subcontractor Invoices</div>
          <div className="pc-value">{money(invoiceTotal)}</div>
          <Link to={`${base}/invoicing`}>{invoices.length} invoice{invoices.length === 1 ? '' : 's'}</Link>
        </div>
        <div className="pc-card pc-stat">
          <div className="pc-label">Owner Billings</div>
          <div className="pc-value">{money(billingTotal)}</div>
          <Link to={`${base}/invoicing`}>{billings.length} application{billings.length === 1 ? '' : 's'}</Link>
        </div>
      </div>
      <div className="pc-home-split">
        <div className="pc-card">
          <div className="pc-panel-h">Recent commitments</div>
          {commitments.length === 0 ? (
            <div className="pc-empty">No commitments on this project yet.</div>
          ) : (
            <table className="pc-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Vendor</th>
                  <th className="pc-right">Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {commitments.slice(0, 8).map((row) => (
                  <tr
                    key={row.vuid}
                    className="pc-clickable"
                    onClick={() => navigate(`${base}/commitments/${row.vuid}`)}
                  >
                    <td>
                      <Link to={`${base}/commitments/${row.vuid}`} onClick={(event) => event.stopPropagation()}>
                        {row.commitment_number}
                      </Link>
                    </td>
                    <td>{row.commitment_name}</td>
                    <td>{row.vendor_name || '—'}</td>
                    <td className="pc-right">{money(row.original_amount || row.commitment_amount)}</td>
                    <td>
                      <span className={`pc-pill ${statusClass(row.status)}`}>{row.status || 'active'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="pc-card">
          <div className="pc-panel-h">Why this preview</div>
          <div className="pc-story">
            Claret is the accounting system of record that sits next to Procore. Field users already live in Procore;
            this layout shows how Claret financials would feel as a native Procore tool.
            <ul>
              <li>Push a commitment or CCO from a Procore workflow — Claret creates the record and writes <code>origin_id</code> back.</li>
              <li>Budgets, invoices, and WIP stay tenant-scoped in Claret.</li>
              <li>Existing Claret screens are unchanged; this is an alternate skin for the pitch.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
